import * as React from 'react';
import { rgba } from 'polished';
import styled, { CSSProperties, keyframes } from 'styled-components';

type RootProps = {
  width?: CSSProperties['width'];
};

type Props = {
  isOpen: boolean;
  width?: CSSProperties['width'];
  onClose: () => void;
  children: React.ReactNode;
};

/* --- アニメーションの追加 --- */
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const slideUp = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`;

const slideDown = keyframes`
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(100%);
  }
`;

const Root = styled.div<RootProps & { isClosing: boolean }>`
  position: relative;
  z-index: 2;
  background-color: ${({ theme }) => theme.backgroundColors.white};
  border-radius: ${({ theme }) => theme.radii.l};
  box-shadow: ${({ theme }) => theme.boxShadow.nav};
  animation: ${({ isClosing }) => (isClosing ? slideDown : slideUp)} 0.4s ease
    forwards;
  width: ${({ width }) => (width ? [width] : '640px')};
  max-width: calc(100vw - 48px);
  max-height: 90vh;
  max-height: 90svh;
  overscroll-behavior: none;
  overflow: auto;
  cursor: auto;
`;

const Overlay = styled.div<Props & { isClosing: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  z-index: 1000;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => rgba(theme.backgroundColors.black, 0.2)};
  backdrop-filter: blur(4px);
  animation: ${({ isClosing }) => (isClosing ? fadeOut : fadeIn)} 0.3s ease
    forwards;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: ${({ theme }) => theme.backgroundColors.white};
  border: 1px solid ${({ theme }) => theme.borderColors.default};
  border-radius: ${({ theme }) => theme.radii.circle};
  transition-duration: ${({ theme }) => theme.transitions.normal};
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.backgroundColors.default};
  }
`;

const CloseIcon = styled.i`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  rotate: 45deg;

  &:before,
  &:after {
    content: '';
    position: absolute;

    width: 16px;
    height: 2px;
    background-color: ${({ theme }) => theme.borderColors.black};
  }

  &:before {
    width: 2px;
    height: 16px;
    translate: 45deg;
  }
  &:after {
    translate: 135deg;
  }
`;

const ModalHeader = styled.div`
  position: sticky;
  top: 0;
  width: 100%;
`;

export const Modal: React.FC<Props> = (props) => {
  const [isClosing, setIsClosing] = React.useState(false);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      props.onClose();
    }, 400);
  };

  if (!props.isOpen && !isClosing) return null;

  return (
    <Overlay
      isClosing={isClosing}
      isOpen={props.isOpen}
      onClose={props.onClose}
      onClick={handleClose}
    >
      <Root
        width={props.width}
        isClosing={isClosing}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <CloseButton onClick={handleClose}>
            <CloseIcon />
          </CloseButton>
        </ModalHeader>
        {props.children}
      </Root>
    </Overlay>
  );
};
